import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { EneLayoutService } from "src/app/core/services/layout.service";
import { select, Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { BehaviorSubject, combineLatest, merge, Observable, of } from "rxjs";
import { map, scan, switchMap } from "rxjs/operators";
import { IButtonToggleItem } from "src/app/common/components/button-toggle/button-toggle.component";
import { IconModule } from "src/app/shared/ui/icon/icon.module";
import { SelectOptionItem } from "src/app/common/components/select/select.interface";
import { PLUGIN } from "src/app/common/enums/plugins";
import { SLOT } from "src/app/common/enums/slot";
import { OverlayControlBaseDirective } from "src/app/components/overlay-control/overlay-control.directive";
import { ContentfulContentType } from "src/app/core/interfaces/contentful";
import { RootState } from "src/app/ngrx/root-reducers";
import { selectRegionEnabledModules } from "src/app/ngrx/selectors/app.selectors";
import { selectLaunchpadOverlay } from "src/app/ngrx/selectors/overlay.selectors";
import { ButtonComponent } from "src/app/shared/ui/button/button.component";
import { ContentfulWrapperNavigationComponent } from "../components/contentful-wrapper-navigation/contentful-wrapper-navigation.component";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "tpm-contentful-wrapper",
  templateUrl: "./contentful-wrapper.component.html",
  styleUrls: ["./contentful-wrapper.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ContentfulWrapperNavigationComponent,
    ButtonComponent,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    IconModule,
    OverlayControlBaseDirective
  ]
})
export class ContentfulWrapperComponent {
  @Input() public headline: string;
  @Input() public contentType: ContentfulContentType;
  @Input() public contentTypeToggles: Array<IButtonToggleItem>;
  @Input() public useChipSet: boolean;
  @Input()
  public set hideModuleSelectOptions(_hideModuleSelectOptions: boolean) {
    this.hideModuleSelectOptionsChange.next(_hideModuleSelectOptions);
  }

  @Output() public readonly searchChange = new EventEmitter<string>();
  @Output() public readonly moduleChange = new EventEmitter<Array<SLOT>>();
  @Output() public readonly contentTypeChange = new EventEmitter<ContentfulContentType>();

  public readonly hideModuleSelectOptionsChange = new BehaviorSubject<boolean>(false);
  public readonly vm$ = merge(
    this.layoutService.isMobileScreen$.pipe(map((isMobileView) => ({ isMobileView }))),
    this.getModuleSelectOptions().pipe(map((moduleSelectOptions) => ({ moduleSelectOptions })))
  ).pipe(scan((vm, values) => ({ ...vm, ...values }), {}));

  constructor(private readonly store: Store<RootState>, private readonly layoutService: EneLayoutService) {}

  private getModuleSelectOptions(): Observable<Array<SelectOptionItem>> {
    return combineLatest([
      this.hideModuleSelectOptionsChange.asObservable(),
      this.store.pipe(select(selectLaunchpadOverlay))
    ]).pipe(
      switchMap(([hideModuleSelectOptions, overlay]) => {
        if (hideModuleSelectOptions) {
          return of([]);
        }

        if (overlay === "module") {
          return this.store.pipe(
            select(selectRegionEnabledModules),
            map((modules) => {
              const elementsForSelection = this.useChipSet ? [...modules] : [undefined, ...modules];

              return elementsForSelection.map((value) => ({
                label: value === undefined ? "CONTENTFUL.none" : `LAUNCHPAD.MODULES.${value}.name`,
                value
              }));
            })
          );
        } else {
          const elementsForSelection: Array<SelectOptionItem> = [
            {
              label: `LAUNCHPAD.PLUGINS.${PLUGIN.STREET_LIGHTING}.name`,
              value: PLUGIN.STREET_LIGHTING
            }
          ];

          if (!this.useChipSet) {
            elementsForSelection.unshift({
              label: "LAUNCHPAD.DIALOGS.ALL_MODULES",
              value: undefined
            });
          }

          return of(elementsForSelection);
        }
      })
    );
  }
}
